import React, { useRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { textFieldValidation } from '@/utils/validations';
import { KeenIcon, Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { HarborTableModal } from './HarborTableModal';
import { HarborResponse } from '@/types/harbor-management.types';
import {
  useGetOperatorCandidatesQuery,
  useGetDmoCandidatesQuery,
} from '@/services/RegionManagementService';

const regionValidationSchema = Yup.object().shape({
  name: textFieldValidation('Region Name', 2, 50, true),
  status: Yup.string()
    .oneOf(['Active', 'Inactive'])
    .required('Status is required'),
  operatorId: Yup.string().when('id', {
    is: (id: string) => !id,
    then: (schema) => schema.required('Region Operator is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  dmoId: Yup.string().notRequired(),
});

interface OperatorOption {
  id: string;
  name: string;
  email?: string;
}

interface DmoOption {
  id: string;
  name: string;
  email?: string;
}

export interface IRegionData {
  id?: string;
  name: string;
  status: 'Active' | 'Inactive';
  operator?: OperatorOption;
  dmo?: DmoOption;
  harborIds?: string[];
  harbors?: HarborResponse[];
  operatorId?: string;
  dmoId?: string;
}

interface RegionFormModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  mode: 'view' | 'edit' | 'create';
  formData: Partial<IRegionData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IRegionData>>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onSubmit: (harborIds?: string[]) => Promise<void>;
  isSubmitting?: boolean;
}

const RegionFormModal = ({
  mode,
  open,
  onClose,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
}: RegionFormModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const operatorDropdownRef = useRef<HTMLDivElement>(null);
  const dmoDropdownRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false);
  const [isDmoDropdownOpen, setIsDmoDropdownOpen] = useState(false);
  const [isHarborModalOpen, setIsHarborModalOpen] = useState(false);
  const { data: operatorUserListing } = useGetOperatorCandidatesQuery({});
  const { data: dmoUserListing } = useGetDmoCandidatesQuery({});

  const validateField = useCallback(
    async (
      name: keyof IRegionData | 'operatorId' | 'dmoId',
      value: unknown
    ) => {
      try {
        await regionValidationSchema.validateAt(name, { [name]: value });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [setErrors]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        operatorDropdownRef.current &&
        !operatorDropdownRef.current.contains(event.target as Node)
      ) {
        setIsOperatorDropdownOpen(false);
      }
      if (
        dmoDropdownRef.current &&
        !dmoDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDmoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus the Name field when the modal opens (except in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open, mode]);

  const handleOperatorSelect = (operator: OperatorOption) => {
    setFormData((prev) => ({ ...prev, operator, operatorId: operator.id }));
    validateField('operatorId', operator.id);
    setIsOperatorDropdownOpen(false);
  };

  const handleDmoSelect = (dmo: DmoOption) => {
    setFormData((prev) => ({ ...prev, dmo, dmoId: dmo.id }));
    validateField('dmoId', dmo.id);
    setIsDmoDropdownOpen(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name as keyof IRegionData, value);
  };

  const handleHarborModalSubmit = (selectedHarborIds: string[]) => {
    setFormData((prev) => ({ ...prev, harborIds: selectedHarborIds }));
    setIsHarborModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await regionValidationSchema.validate(formData, { abortEarly: false });
      await onSubmit(formData.harborIds);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar('An error occurred', { variant: 'error' });
      } else {
        enqueueSnackbar('An error occurred', { variant: 'error' });
      }
    }
  };

  return (
    <>
      <Modal
        open={open && !isHarborModalOpen}
        onClose={onClose}
        className={clsx('!flex items-center justify-center', className)}
        aria-labelledby="region-form-modal-title"
        data-testid="region-form-modal"
      >
        <div className="bg-gray-100 p-6 w-full max-w-md mx-4 rounded-lg shadow-lg flex flex-col max-h-[97dvh] overflow-y-auto">
          <h1
            className="text-lg font-medium text-gray-900 mb-4"
            id="region-form-modal-title"
            data-testid="modal-title"
          >
            {mode === 'create'
              ? 'Create Region'
              : mode === 'edit'
                ? 'Edit Region'
                : 'View Region'}
          </h1>
          <div className="flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar">
            <div className="flex flex-col gap-4 pr-3">
              <div>
                {mode === 'view' ? (
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Region Name
                  </span>
                ) : (
                  <label
                    htmlFor="name-input"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Region Name
                  </label>
                )}
                <input
                  id="name-input"
                  data-testid="name-input"
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  ref={nameInputRef}
                  onChange={handleInputChange}
                  onKeyDown={preventLeadingWhitespace}
                  onBlur={() => validateField('name', formData.name)}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-10',
                    errors.name && 'border-red-500'
                  )}
                  placeholder="Enter region name"
                  aria-label="Region Name"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p
                    id="name-error"
                    className="text-red-500 text-sm mt-1"
                    data-testid="name-error"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                {mode === 'view' ? (
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </span>
                ) : (
                  <label
                    htmlFor="status-input"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Status
                  </label>
                )}
                <select
                  id="status-input"
                  data-testid="status-input"
                  name="status"
                  value={formData.status || ''}
                  onChange={handleInputChange}
                  onBlur={() => validateField('status', formData.status)}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'select select-sm w-full h-10',
                    errors.status && 'border-red-500'
                  )}
                  aria-label="Status"
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                >
                  <option value="" disabled>
                    Select status
                  </option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && (
                  <p
                    id="status-error"
                    className="text-red-500 text-sm mt-1"
                    data-testid="status-error"
                  >
                    {errors.status}
                  </p>
                )}
              </div>
              <div>
                {mode === 'view' ? (
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Region Operator
                  </span>
                ) : (
                  <label
                    htmlFor="operator-id"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Region Operator
                  </label>
                )}
                <div className="relative" ref={operatorDropdownRef}>
                  <div
                    id="operator-id"
                    data-testid="operator-input"
                    className={clsx(
                      'input input-bordered w-full h-10 flex items-center justify-between cursor-pointer',
                      isOperatorDropdownOpen &&
                        mode !== 'view' &&
                        'border-primary',
                      errors.operatorId && 'border-red-500',
                      mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                    )}
                    onClick={() =>
                      mode !== 'view' &&
                      setIsOperatorDropdownOpen((prev) => !prev)
                    }
                    onKeyDown={(e) => {
                      if (mode === 'view') return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsOperatorDropdownOpen((prev) => !prev);
                      } else if (e.key === 'Escape' && isOperatorDropdownOpen) {
                        setIsOperatorDropdownOpen(false);
                      } else if (
                        e.key === 'ArrowDown' &&
                        !isOperatorDropdownOpen
                      ) {
                        e.preventDefault();
                        setIsOperatorDropdownOpen(true);
                      }
                    }}
                    tabIndex={mode === 'view' ? -1 : 0}
                    aria-label="Select operator"
                    aria-invalid={!!errors.operatorId}
                    aria-expanded={isOperatorDropdownOpen}
                    aria-controls="operator-options-listbox"
                    role="combobox"
                  >
                    <span>
                      {formData.operator?.email || 'Select region operator'}
                      {formData.operator?.name
                        ? ` (${formData.operator.name})`
                        : ''}
                    </span>
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400',
                        isOperatorDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {isOperatorDropdownOpen && mode !== 'view' && (
                    <div
                      id="operator-options-listbox"
                      className="absolute z-50 w-full bottom-[110%] bg-white border border-gray-300 rounded-md shadow-lg max-h-[170px] overflow-y-auto"
                      role="listbox"
                      aria-label="Region operator options"
                    >
                      {Array.isArray(operatorUserListing?.data) &&
                      operatorUserListing.data.length > 0 ? (
                        operatorUserListing.data.map(
                          (operator: OperatorOption, index: number) => (
                            <div
                              key={operator.id}
                              data-testid={`operator-option-${operator.id}`}
                              className={clsx(
                                'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:bg-black dark:text-white contrast:bg-black contrast:text-white contrast:hover:bg-black/80 dark:hover:bg-black/80',
                                formData.operator?.id === operator.id &&
                                  'bg-primary-light'
                              )}
                              onClick={() => handleOperatorSelect(operator)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleOperatorSelect(operator);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const operatorOptions =
                                    operatorUserListing.data as unknown as OperatorOption[];
                                  const nextIndex =
                                    (index + 1) % operatorOptions.length;
                                  const nextElement = e.currentTarget
                                    .parentElement?.children[
                                    nextIndex
                                  ] as HTMLElement;
                                  nextElement?.focus();
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const operatorOptions =
                                    operatorUserListing.data as unknown as OperatorOption[];
                                  const prevIndex =
                                    index === 0
                                      ? operatorOptions.length - 1
                                      : index - 1;
                                  const prevElement = e.currentTarget
                                    .parentElement?.children[
                                    prevIndex
                                  ] as HTMLElement;
                                  prevElement?.focus();
                                } else if (e.key === 'Escape') {
                                  setIsOperatorDropdownOpen(false);
                                  // Focus back to the combobox
                                  const combobox = e.currentTarget
                                    .closest('.relative')
                                    ?.querySelector(
                                      '[role="combobox"]'
                                    ) as HTMLElement;
                                  combobox?.focus();
                                }
                              }}
                              tabIndex={0}
                              role="option"
                              aria-selected={
                                formData.operator?.id === operator.id
                              }
                            >
                              {operator.email}
                              {operator.name ? ` (${operator.name})` : ''}
                            </div>
                          )
                        )
                      ) : (
                        <div
                          className="px-3 py-2 text-sm text-gray-500"
                          data-testid="no-operators"
                        >
                          No operators found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.operatorId && (
                  <p
                    id="operator-error"
                    className="text-red-500 text-sm mt-1"
                    data-testid="operator-error"
                  >
                    {errors.operatorId}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {mode === 'view' ? (
                  <span className="text-sm font-medium mb-1 block">
                    DMO Admin
                  </span>
                ) : (
                  <label
                    htmlFor="dmo-id"
                    className="text-sm font-medium mb-1 block"
                  >
                    DMO Admin
                  </label>
                )}
                <div className="relative" ref={dmoDropdownRef}>
                  <div
                    id="dmo-id"
                    data-testid="dmo-dropdown-button"
                    className={clsx(
                      'input input-bordered w-full h-10 flex items-center justify-between cursor-pointer ',
                      isDmoDropdownOpen && mode !== 'view' && 'border-primary',
                      errors.dmoId && 'border-red-500',
                      mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                    )}
                    onClick={() =>
                      mode !== 'view' &&
                      setIsDmoDropdownOpen(!isDmoDropdownOpen)
                    }
                    onKeyDown={(e) => {
                      if (mode === 'view') return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsDmoDropdownOpen(!isDmoDropdownOpen);
                      } else if (e.key === 'Escape' && isDmoDropdownOpen) {
                        setIsDmoDropdownOpen(false);
                      } else if (e.key === 'ArrowDown' && !isDmoDropdownOpen) {
                        e.preventDefault();
                        setIsDmoDropdownOpen(true);
                      }
                    }}
                    tabIndex={mode === 'view' ? -1 : 0}
                    aria-label="Select DMO admin"
                    aria-invalid={!!errors.dmoId}
                    aria-expanded={isDmoDropdownOpen}
                    aria-controls="dmo-options-listbox"
                    role="combobox"
                  >
                    <span>
                      {formData.dmo?.email || 'Select DMO Admin'}
                      {formData.dmo?.name ? ` (${formData.dmo.name})` : ''}
                    </span>
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400',
                        isDmoDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {isDmoDropdownOpen && mode !== 'view' && (
                    <div
                      id="dmo-options-listbox"
                      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                      role="listbox"
                      aria-label="DMO Admin options"
                      data-testid="dmo-dropdown-options"
                    >
                      {Array.isArray(dmoUserListing?.data) &&
                      dmoUserListing.data.length > 0 ? (
                        dmoUserListing.data.map(
                          (dmo: DmoOption, index: number) => (
                            <div
                              key={dmo.id}
                              data-testid={`dmo-option-${dmo.id}`}
                              className={clsx(
                                'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:bg-black dark:text-white contrast:bg-black contrast:text-white contrast:hover:bg-black/80 dark:hover:bg-black/80',
                                formData.dmo?.id === dmo.id &&
                                  'bg-primary-light'
                              )}
                              onClick={() => handleDmoSelect(dmo)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleDmoSelect(dmo);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const dmoOptions =
                                    dmoUserListing.data as unknown as DmoOption[];
                                  const nextIndex =
                                    (index + 1) % dmoOptions.length;
                                  const nextElement = e.currentTarget
                                    .parentElement?.children[
                                    nextIndex
                                  ] as HTMLElement;
                                  nextElement?.focus();
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const dmoOptions =
                                    dmoUserListing.data as unknown as DmoOption[];
                                  const prevIndex =
                                    index === 0
                                      ? dmoOptions.length - 1
                                      : index - 1;
                                  const prevElement = e.currentTarget
                                    .parentElement?.children[
                                    prevIndex
                                  ] as HTMLElement;
                                  prevElement?.focus();
                                } else if (e.key === 'Escape') {
                                  setIsDmoDropdownOpen(false);
                                  // Focus back to the combobox
                                  const combobox = e.currentTarget
                                    .closest('.relative')
                                    ?.querySelector(
                                      '[role="combobox"]'
                                    ) as HTMLElement;
                                  combobox?.focus();
                                }
                              }}
                              tabIndex={0}
                              role="option"
                              aria-selected={formData.dmo?.id === dmo.id}
                            >
                              {dmo.email}
                              {dmo.name ? ` (${dmo.name})` : ''}
                            </div>
                          )
                        )
                      ) : (
                        <div
                          className="px-3 py-2 text-sm text-gray-500"
                          data-testid="no-dmos"
                        >
                          No DMOs found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.dmoId && (
                  <p
                    id="dmo-error"
                    className="text-red-500 text-sm mt-1"
                    data-testid="dmo-error"
                  >
                    {errors.dmoId}
                  </p>
                )}
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setIsHarborModalOpen(true)}
                  className="btn btn-sm btn-light w-max-content ml-2 mb-2"
                  disabled={isSubmitting}
                  data-testid="harbors-button"
                >
                  <KeenIcon
                    icon={
                      mode === 'create'
                        ? 'plus'
                        : mode === 'edit'
                          ? 'notepad-edit'
                          : 'eye'
                    }
                  />
                  {mode === 'create'
                    ? 'Add Harbors'
                    : mode === 'edit'
                      ? 'Edit Harbors'
                      : 'View Harbors'}
                </button>
              </div>
            </div>
          </div>
          {(mode === 'create' || mode === 'edit') && (
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onClose?.({}, 'backdropClick')}
                className="btn btn-sm btn-light"
                disabled={isSubmitting}
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-sm btn-primary"
                disabled={isSubmitting}
                data-testid="submit-button"
              >
                {isSubmitting
                  ? 'Saving...'
                  : mode === 'create'
                    ? 'Create'
                    : 'Save'}
              </button>
            </div>
          )}
        </div>
      </Modal>
      <HarborTableModal
        open={isHarborModalOpen}
        onClose={() => setIsHarborModalOpen(false)}
        mode={mode}
        selectedHarborIds={formData.harborIds || []}
        onSubmit={handleHarborModalSubmit}
        regionId={formData.id}
        regionHarbors={formData.harbors}
      />
    </>
  );
};

export { RegionFormModal };
